/**
 * Provider Resource Mapper
 * Converts Provider resources into timeline events
 *
 * This file contains the main getProviderItem function and helpers for each resource type
 */

import {
    CurrencyType,
    ITimelineEvent,
    parseAmount,
    PaymentStatusType,
    ProviderClaim,
    ProviderDisplayDocumentType,
    ProviderInstitutionalClaimTemplate,
    ProviderNynf3ClaimTemplate,
    ProviderPayerSubscriber,
    ProviderProfessionalClaimTemplate,
    ProviderSubscriberPatient,
    renderAmount,
    renderDate,
    TransmissionStatusBadge,
} from '@enter/core';
import {
    mdiAlphaCCircleOutline,
    mdiAlphaSCircleOutline,
    mdiBeakerOutline,
    mdiCached,
    mdiCommentOutline,
    mdiCurrencyUsd,
    mdiEmail,
    mdiEmailCheckOutline,
    mdiEmailOpenOutline,
    mdiFileDocument,
    mdiLockOpenVariantOutline,
    mdiLockOutline,
    mdiMessageOutline,
    mdiMessageTextOutline,
    mdiCodeTags,
    mdiPaperclip,
    mdiPhoneVoip,
    mdiReceipt,
    mdiCheckCircle,
    mdiFileCheckOutline,
} from '@mdi/js';
import { getNickname } from '@enter/core/src/ui/messaging/util';
import _ from 'lodash';
import React from 'react';
import Icon from '@mdi/react';
import styled from '@emotion/styled/macro';
import { Variables } from '@enter/core';
import { ClaimDetail } from '../claim-timeline-table/claim-detail';
import {
    CodingDetail,
    CollectionPaymentDetail,
    CommentDetail,
    DocumentDetail,
    EmailDetail,
    FaxDetail,
    HL7Detail,
    InvoiceDetail,
    LetterDetail,
    MailDetail,
    PaymentDetail,
    PhoneDetail,
    StatusItemsDetail,
    TransmissionDetail,
    TransmissionStatus,
    EligibilityTimelineDetail,
    EligibilityTimelineDescription,
    ClaimDataDetail,
    isClaimDataHl7Message,
} from '@enter/core';
import { PaymentItems } from '@enter/core/src/ui/claim-details/claim-timeline/items/payment-items';
import { ReceiveFaxDetail } from '../claim-timeline-table/receive-fax-detail';
import { ProviderClaimResource } from './resource-types';

// Styled components for descriptions
const StatusIcon = styled(Icon)`
    margin-right: 8px;
    flex-shrink: 0;
`;

const MessageDescription = styled.div`
    display: flex;
    align-items: center;
`;

const Attachment = styled.span`
    display: inline-flex;
    align-items: center;
`;

const AttachIcon = styled(Icon)`
    transform: rotate(45deg);
    margin-right: 4px;
    fill: ${Variables.Colors.n30};
`;

const DescriptionItem = styled.span`
    &:not(:first-of-type) {
        margin-left: 8px;
    }
`;

const AmountDisplayRow = React.memo<{ label: string; value: any }>(({ label, value }) => {
    if (!value) {
        return null;
    }
    return (
        <div style={{ marginBottom: '4px' }}>
            <span style={{ fontWeight: 500 }}>{label}: </span>
            <span
                style={{
                    fontWeight: 600,
                    color: Variables.ThemeLight.TextStrong,
                    fontVariantNumeric: 'tabular-nums',
                }}>
                {renderAmount(value)}
            </span>
        </div>
    );
});

const TimelineDescriptionAmount = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    font-variant-numeric: tabular-nums;
`;

/**
 * Additional context needed by event builders
 */
export interface IProviderItemContext {
    claim?: ProviderClaim;
    claimPind?: any;
    template?:
        | ProviderProfessionalClaimTemplate
        | ProviderInstitutionalClaimTemplate
        | ProviderNynf3ClaimTemplate;
    primaryPayerSubscriber?: ProviderPayerSubscriber;
    primarySubscriberPatient?: ProviderSubscriberPatient;
    letters?: any[];
    mails?: any[];
    receiveFaxes?: any[];
    sendFaxes?: any[];
    payments?: any[];
    transactions?: any[];
    paymentPlans?: any[];
    subscriberPatients?: ProviderSubscriberPatient[];
    messageStore?: {
        getCategoryName: (categoryId: string) => string | undefined;
    };
    onOpenMessage?: (message: any) => void;
    onCreatePaymentPlan?: (invoiceId: string) => void;
    claimStore?: {
        viewLetterById: (letterId: string) => Promise<{ url?: string } | undefined>;
        loadDocumentDownloadUrl: (
            documentId: string,
            documentType?: ProviderDisplayDocumentType,
        ) => Promise<{ url?: string } | undefined>;
        getDocumentTranscript: (documentId: string) => Promise<any>;
        viewFaxById: (faxId: string) => Promise<{ url?: string } | undefined>;
        findPayerSubscriber?: (payerSubscriberId: string) => any;
        loadMailCart?: (mailCartId: string) => Promise<any>;
    };
    resources?: any[];
    rootStore?: {
        claimStore: {
            loadPaymentDownloadUrl: (paymentId: string) => Promise<{ url?: string } | undefined>;
        };
    };
    hl7TimelineCache?: IHl7TimelineCache;
}

function isProviderClaimUpdateType(type: string | undefined): boolean {
    return type === 'JSON_PROVIDER_PROFESSIONAL' || type === 'JSON_PROVIDER_INSTITUTIONAL';
}

interface IHl7TimelineCache {
    sortedDesc: any[];
    claimDataIndexById: Map<string, number>;
}

function getHl7TimelineCache(context: IProviderItemContext): IHl7TimelineCache {
    if (context.hl7TimelineCache) {
        return context.hl7TimelineCache;
    }

    const hl7Messages = (context.resources || []).filter((r: any) => r.resourceType === 'HL7');
    const sortedDesc = _.orderBy(hl7Messages, ['created'], ['desc']);
    const claimDataUpdates = _.orderBy(
        hl7Messages.filter((r: any) => !isProviderClaimUpdateType(r.type)),
        ['created'],
        ['asc'],
    );
    const claimDataIndexById = new Map<string, number>(
        claimDataUpdates.map((message: any, index: number) => [message.id, index + 1]),
    );

    context.hl7TimelineCache = { sortedDesc, claimDataIndexById };
    return context.hl7TimelineCache;
}

/**
 * Main mapper function that converts a Provider resource to a timeline event
 */
export function getProviderItem(resource: ProviderClaimResource, context: IProviderItemContext): ITimelineEvent {
    switch (resource.resourceType) {
        case 'CODING':
            return getCodingEvent(resource);
        case 'COMMENT':
            return getCommentEvent(resource);
        case 'DOCUMENT':
            return getDocumentEvent(resource, context);
        case 'ELIGIBILITY':
            return getEligibilityEvent(resource);
        case 'EMAIL_SEND':
            return getEmailEvent(resource);
        case 'FAX_RECEIVE':
            return getFaxReceiveEvent(resource);
        case 'FAX_SEND':
            return getFaxSendEvent(resource, context);
        case 'HL7':
            return getHL7Event(resource, context);
        case 'INVOICE':
            return getInvoiceEvent(resource, context);
        case 'LETTER':
            return getLetterEvent(resource, context);
        case 'MAIL':
            return getMailEvent(resource, context);
        case 'MESSAGE':
            return getMessageEvent(resource, context);
        case 'PAYMENT':
            return getPaymentEvent(resource, context);
        case 'PHONE':
            return getPhoneEvent(resource, context);
        case 'COLLECTION_PAYMENT':
            return getCollectionEvent(resource, context);
        case 'STATUS':
            return getStatusEvent(resource);
        case 'TRANSACTION':
            return getTransactionEvent(resource);
        case 'TRANSMISSION':
            return getTransmissionEvent(resource, context);
        case 'STEP':
            return getStepEvent(resource, context);
        default:
            // Should never reach here due to exhaustive type checking
            return {
                id: 'unknown',
                created: new Date().toISOString(),
                event: 'Unknown Event',
                description: 'Unknown resource type',
                type: 'Enter Admin',
            };
    }
}

// ============================================================================
// Event Builders for Each Resource Type
// ============================================================================

function getCodingEvent(resource: any): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Coding',
        icon: mdiCodeTags,
        description: `Status: ${resource.status}`,
        details: <CodingDetail coding={resource} embedded />,
        kind: 'coding',
        type: 'Enter Admin',
    };
}

function getCommentEvent(resource: any): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Comment',
        icon: mdiCommentOutline,
        description: `Author: ${getNickname(resource.author) || 'Unknown'}`,
        details: <CommentDetail comment={resource} />,
        type: 'Enter Admin',
    };
}

function getDocumentEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const { letters, mails, receiveFaxes, sendFaxes, claimStore } = context;

    // Check if this document is linked to a letter, mail, or fax
    const letter = _.find(letters, { documentId: resource.id });
    if (letter) {
        return getLetterEvent(letter, context);
    }

    const mailItem = _.find(mails, { documentId: resource.id });
    if (mailItem) {
        return getMailEvent(mailItem, context);
    }

    const receiveFaxItem = _.find(receiveFaxes, { documentId: resource.id });
    if (receiveFaxItem) {
        return getFaxReceiveEvent(receiveFaxItem);
    }

    const sendFaxItem = _.find(sendFaxes, { documentId: resource.id });
    if (sendFaxItem) {
        return getFaxSendEvent(sendFaxItem, context);
    }

    // Default document event
    return {
        id: resource.id,
        created: resource.created,
        event: 'Documents Added',
        icon: mdiFileDocument,
        description: (
            <Attachment>
                <AttachIcon path={mdiPaperclip} size={0.75} />
                <div>{resource.filename}</div>
            </Attachment>
        ),
        details: (
            <DocumentDetail
                document={resource}
                appType="provider"
                onLoadDocumentUrl={async (documentId, documentType) => {
                    if (!claimStore?.loadDocumentDownloadUrl) {
                        return { url: undefined };
                    }
                    return await claimStore.loadDocumentDownloadUrl(
                        documentId,
                        documentType as ProviderDisplayDocumentType,
                    );
                }}
            />
        ),
        type: 'Enter Admin',
    };
}

function getEligibilityEvent(resource: any): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Eligibility Verified',
        icon: mdiFileCheckOutline,
        description: <EligibilityTimelineDescription eligibility={resource} />,
        details: <EligibilityTimelineDetail eligibility={resource} />,
        kind: 'eligibility',
        type: 'Enter Admin',
    };
}

function getEmailEvent(resource: any): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Email Sent',
        icon: mdiEmail,
        description: (
            <Attachment>
                <AttachIcon path={mdiEmail} size={0.75} />
                <div>{resource.subject}</div>
            </Attachment>
        ),
        details: <EmailDetail email={resource} appType="provider" />,
        type: 'Enter Admin',
    };
}

function getFaxReceiveEvent(resource: any): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Fax Received',
        icon: mdiEmailCheckOutline,
        description: `Fax received from ${resource.fromE164}`,
        details: <ReceiveFaxDetail receiveFax={resource} />,
        type: 'Enter Admin',
    };
}

function getFaxSendEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const { claimStore } = context;

    return {
        id: resource.id,
        created: resource.created,
        event: 'Fax Sent',
        icon: mdiEmailCheckOutline,
        description: resource.toE164 ? `Fax sent to ${resource.toE164}` : 'Fax sent',
        details: (
            <FaxDetail fax={resource} appType="provider" downloadDocument={claimStore.viewFaxById.bind(claimStore)} />
        ),
        type: 'Enter Admin',
    };
}

function getHL7Event(resource: any, context: IProviderItemContext): ITimelineEvent {
    const isProviderClaimUpdate = isProviderClaimUpdateType(resource.type);
    const isClaimData = isClaimDataHl7Message(resource.type);
    const { sortedDesc, claimDataIndexById } = getHl7TimelineCache(context);

    const eventLabel = isProviderClaimUpdate
        ? 'Provider Claim Update'
        : isClaimData
          ? `Claim Data (${claimDataIndexById.get(resource.id) ?? 1})`
          : 'Integration Message';

    const currentIndex = sortedDesc.findIndex((m: any) => m.id === resource.id);
    const previousMessage = currentIndex >= 0 ? sortedDesc[currentIndex + 1] : undefined;

    return {
        id: resource.id,
        created: resource.created,
        event: eventLabel,
        icon: mdiMessageTextOutline,
        description: resource.type?.startsWith('JSON_')
            ? undefined
            : resource.type || `Message received ${renderDate(resource.created)}`,
        details: isClaimData ? (
            <ClaimDataDetail claimData={resource} embedded appType="provider" />
        ) : (
            <HL7Detail hl7Message={resource} before={previousMessage} appType="provider" />
        ),
        kind: isClaimData ? 'claimdata' : undefined,
        type: 'External System',
    };
}

function getInvoiceEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const paymentPlansForInvoice = _.filter(context.paymentPlans || [], plan => plan.invoiceId === resource.id);

    return {
        id: resource.id,
        created: resource.created,
        event: 'Invoice',
        icon: mdiReceipt,
        description: (
            <>
                Invoice: <TimelineDescriptionAmount>{renderAmount(resource.amount)}</TimelineDescriptionAmount>
            </>
        ),
        details: (
            <InvoiceDetail
                invoice={resource}
                appType="provider"
                transactions={context.transactions || []}
                payments={context.payments || []}
                paymentPlans={paymentPlansForInvoice}
                onCreatePaymentPlan={
                    context.onCreatePaymentPlan ? () => context.onCreatePaymentPlan!(resource.id) : undefined
                }
            />
        ),
        type: 'Enter Admin',
    };
}

// Wrapper component for Provider LetterDetail that handles document loading
const ProviderLetterDetailWrapper: React.FC<{ letter: any; claimStore?: IProviderItemContext['claimStore'] }> = ({
    letter,
    claimStore,
}) => {
    const [loading, setLoading] = React.useState(false);
    const [documentUrl, setDocumentUrl] = React.useState<string | undefined>();

    React.useEffect(() => {
        (async (): Promise<void> => {
            if (letter?.id && letter.document?.id && claimStore) {
                setLoading(true);
                try {
                    const url = await claimStore.viewLetterById(letter.id);
                    setDocumentUrl(url?.url);
                } catch (error) {
                    console.error('Error loading letter document:', error);
                } finally {
                    setLoading(false);
                }
            }
        })();
    }, [letter, claimStore]);

    return <LetterDetail letter={letter} appType="provider" documentUrl={documentUrl} loading={loading} />;
};

function getLetterEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Letter',
        icon: mdiEmailCheckOutline,
        description: `Letter sent ${renderDate(_.get(resource, 'response.sendDate'))}`,
        details: <ProviderLetterDetailWrapper letter={resource} claimStore={context.claimStore} />,
        type: 'Enter Admin',
    };
}

function getMailEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const { claimStore } = context;
    return {
        id: resource.id,
        created: resource.created,
        event: 'Mail',
        icon: mdiEmailOpenOutline,
        description: `Mail received ${renderDate(resource.receivedDate)}`,
        details: (
            <MailDetail
                mail={resource}
                appType="provider"
                downloadDocument={claimStore.loadDocumentDownloadUrl.bind(claimStore)}
                loadMailCart={claimStore.loadMailCart?.bind(claimStore)}
            />
        ),
        type: 'Enter Admin',
    };
}

function getMessageEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const isOpen = resource.open ?? false;
    const statusTooltip = isOpen ? 'Open' : 'Closed';
    const statusIcon = isOpen ? mdiLockOpenVariantOutline : mdiLockOutline;

    // Get category names
    const categoryNames = context.messageStore
        ? _.compact(
              _.map(resource.links?.messageCategories || [], cat => {
                  if (context.messageStore?.getCategoryName && cat.id) {
                      return context.messageStore.getCategoryName(cat.id);
                  }
                  return undefined;
              }),
          )
        : [];

    return {
        id: resource.id,
        created: resource.created,
        updated: resource.updated,
        event: 'Message',
        icon: mdiMessageOutline,
        description: (
            <MessageDescription>
                <StatusIcon path={statusIcon} size={0.8} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span>{resource.subject}</span>
                    {categoryNames.length > 0 && <div>Categories: {categoryNames.join(', ')}</div>}
                </div>
            </MessageDescription>
        ),
        onClick: context.onOpenMessage ? () => context.onOpenMessage!(resource) : undefined,
        type: resource.author?.nickname || resource.author?.type || 'Enter Admin',
    };
}

function getPaymentEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const event = resource.status === PaymentStatusType.REVERSALOFPREVIOUSPAYMENT ? 'Reversal' : 'Payer Payment';
    const { payerAmount, patientAmount, chargeAmount, allowedAmount } = resource;

    // Service lines + payment-level adjustments (same tables as Support)
    const serviceLinesContent =
        resource.items?.length || resource.adjustments?.length ? (
            <PaymentItems items={resource.items ?? []} paymentAdjustments={resource.adjustments} />
        ) : undefined;

    return {
        id: resource.id,
        created: resource.created,
        event,
        icon: mdiCurrencyUsd,
        description: (
            <div style={{ width: '100%' }}>
                <AmountDisplayRow label="Allowed Amount" value={allowedAmount} />
                {parseAmount(payerAmount) > 0 && <AmountDisplayRow label="Payer Amount" value={payerAmount} />}
                {parseAmount(patientAmount) > 0 && <AmountDisplayRow label="Patient Amount" value={patientAmount} />}
                {parseAmount(chargeAmount) > 0 && <AmountDisplayRow label="Charge Amount" value={chargeAmount} />}
            </div>
        ),
        details: (
            <PaymentDetail
                payment={resource}
                appType="provider"
                onLoadPaymentDocumentUrl={
                    context.rootStore?.claimStore
                        ? async (paymentId: string) => {
                              const url = await context.rootStore!.claimStore.loadPaymentDownloadUrl(paymentId);
                              return { url: url?.url };
                          }
                        : undefined
                }
                serviceLines={serviceLinesContent}
            />
        ),
        type: 'Enter Admin',
    };
}

function getPhoneEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const { claimStore } = context;

    return {
        id: resource.id,
        created: resource.created,
        event: 'Phone',
        icon: mdiPhoneVoip,
        description: 'Payer Phone Call',
        details: (
            <PhoneDetail
                phone={resource}
                appType="provider"
                downloadDocument={claimStore.loadDocumentDownloadUrl.bind(claimStore)}
                transcribeDocument={claimStore.getDocumentTranscript.bind(claimStore)}
            />
        ),
        type: 'Enter Admin',
    };
}

function getCollectionEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Patient Payment',
        icon: mdiCurrencyUsd,
        description: (
            <div style={{ width: '100%' }}>
                <AmountDisplayRow label="Collected Amount" value={resource.collectedAmount} />
                <AmountDisplayRow label="Refunded Amount" value={resource.refundedAmount} />
            </div>
        ),
        details: <CollectionPaymentDetail payment={resource} appType="provider" />,
        type: 'Enter Admin',
    };
}

function getStatusEvent(resource: any): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Status Change',
        icon: mdiAlphaSCircleOutline,
        description: `${_.size(resource.items)} items`,
        details: <StatusItemsDetail status={resource} />,
        type: 'Enter Admin',
    };
}

function getTransactionEvent(resource: any): ITimelineEvent {
    return {
        id: resource.id,
        created: resource.created,
        event: 'Transaction',
        icon: mdiBeakerOutline,
        description: (
            <>
                {resource.type}:{' '}
                <TimelineDescriptionAmount>{renderAmount(resource.amount)}</TimelineDescriptionAmount>
            </>
        ),
        type: 'Enter Admin',
    };
}

function getTransmissionEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const clmMatch = (resource.x12 || '').match(/CLM\*.*\n/g);
    let amount = '0';
    if (clmMatch && !_.isEmpty(clmMatch)) {
        const clmList = clmMatch[0].split('*');
        amount = clmList[2] ? parseFloat(clmList[2]).toFixed(2) : '0';
    }

    const sortedTransmissions = (context.resources || [])
        .filter((r: any) => r.resourceType === 'TRANSMISSION')
        .sort((a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime());
    const currentIndex = sortedTransmissions.findIndex((t: any) => t.id === resource.id);
    const previousTransmission =
        currentIndex >= 0 && currentIndex < sortedTransmissions.length - 1
            ? sortedTransmissions[currentIndex + 1]
            : undefined;

    const latestTransmissionStatus = resource.statuses?.[0];
    const lastStatus = latestTransmissionStatus?.status;
    const { claimStore } = context;

    return {
        id: resource.id,
        created: resource.created,
        event: 'Claim Transmission',
        icon: mdiCached,
        description: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {lastStatus ? (
                    <TransmissionStatusBadge
                        status={lastStatus}
                        messageCode={latestTransmissionStatus?.messageCode}
                        size="large"
                    />
                ) : (
                    <span>No statuses</span>
                )}
                <TimelineDescriptionAmount>
                    {renderAmount({ amount, currency: CurrencyType.USD })}
                </TimelineDescriptionAmount>
            </div>
        ),
        secondaryType: <TransmissionStatus transmission={resource} />,
        details: claimStore ? (
            <TransmissionDetail
                transmission={resource}
                previousTransmission={previousTransmission}
                appType="provider"
                embedded
                downloadDocument={claimStore.loadDocumentDownloadUrl.bind(claimStore)}
                getPayerSubscriber={
                    claimStore.findPayerSubscriber
                        ? (id: string) => Promise.resolve(claimStore.findPayerSubscriber!(id))
                        : undefined
                }
            />
        ) : null,
        kind: 'transmission',
        type: 'Enter Admin',
    };
}

function getStepEvent(resource: any, context: IProviderItemContext): ITimelineEvent {
    const { claim, claimPind, primaryPayerSubscriber, primarySubscriberPatient } = context;

    // Step event icons based on type
    const stepIcons = {
        CREATED: mdiAlphaCCircleOutline,
        SUBMITTED: mdiCached,
        RECEIVED: mdiCurrencyUsd,
        FINALIZED: mdiCheckCircle,
    };

    // Only Claim Created has a detail component
    if (resource.stepType === 'CREATED' && claim && claimPind) {
        return {
            id: resource.id,
            created: resource.created,
            event: resource.event,
            icon: stepIcons[resource.stepType] || mdiAlphaCCircleOutline,
            description: resource.description,
            type: resource.type,
            kind: 'generic',
            details: (
                <ClaimDetail
                    claimId={claim.id}
                    performedBy={resource.type}
                    claimPind={claimPind}
                    primaryPayerSubscriber={primaryPayerSubscriber}
                    primarySubscriberPatient={primarySubscriberPatient}
                    template={context.template}
                />
            ),
        };
    }

    // Other step events don't have details
    return {
        id: resource.id,
        created: resource.created,
        event: resource.event,
        icon: stepIcons[resource.stepType] || mdiAlphaCCircleOutline,
        description: resource.description,
        type: resource.type,
    };
}
